import { clamp } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

type Opts = {
  itemsCount: number;
  maxRows?: number;
  minColWidth?: number;
  maxColWidth?: number;
  minRowHeight?: number;
  gap?: number;
  hardCapRows?: number;
};

type Result = {
  rows: number;
  colWidth: number;
  colsNeeded: number;
  refCallback: (el: HTMLElement | null) => void;
};

// cSpell:ignore recalc
//TODO consider to merge with useCaseCardLayout
export const useAirFleetCardLayout = ({
  itemsCount,
  maxRows = 0,
  minColWidth = 200,
  maxColWidth = 250,
  minRowHeight = 260,
  gap = 8,
  hardCapRows = 10,
}: Opts): Result => {
  const elRef = useRef<HTMLElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const [rows, setRows] = useState(1);
  const [colWidth, setColWidth] = useState(maxColWidth);
  const [colsNeeded, setColsNeeded] = useState(1);

  const recalc = useCallback(() => {
    const el = elRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const W = el.clientWidth || rect.width;
    const H = el.clientHeight || rect.height;
    // available height inside scrollable ancestor, parent or viewport
    // TODO: revisit getAvailableHeight logic if we find cases where it fails
    // const H = getAvailableHeight(el);

    // rows we could fit by height (>=1)
    const fitByHeight = Math.max(
      1,
      Math.floor((H + gap) / (minRowHeight + gap)),
    );

    // sanitize user prop: allow 0/undefined to mean "auto"
    const desired = maxRows && maxRows > 0 ? maxRows : Number.POSITIVE_INFINITY;

    // absolute upper bounds
    const maxByItems = Math.max(1, Math.min(itemsCount, hardCapRows));
    const maxByHeight = Math.max(1, Math.min(fitByHeight, hardCapRows));

    // SAFE rows we will actually use
    const safeRows = Math.max(1, Math.min(desired, maxByItems, maxByHeight));

    // columns needed to place all items into `safeRows`
    const neededCols = Math.max(1, Math.ceil(itemsCount / safeRows));

    // exact column width for that many columns (before clamping)
    const exactCol = (W - (neededCols - 1) * gap) / neededCols;

    // clamp to allowed width range; if we clamp to min, horizontal scroll is expected
    const clampedCol = clamp(exactCol, minColWidth, maxColWidth);

    setRows(safeRows);
    setColsNeeded(neededCols);
    setColWidth(clampedCol);
  }, [
    gap,
    itemsCount,
    maxColWidth,
    minColWidth,
    minRowHeight,
    maxRows,
    hardCapRows,
  ]);

  // Recalc when inputs change - trigger via timeout to avoid cascading renders
  useEffect(() => {
    // Use setTimeout to defer calculation after render completes
    const timeoutId = setTimeout(() => recalc(), 0);
    return () => clearTimeout(timeoutId);
  }, [recalc]);

  const refCallback = useCallback(
    (el: HTMLElement | null) => {
      // Disconnect old observer
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }

      elRef.current = el;

      if (el) {
        // Attach fresh observer to the current element
        const ro = new ResizeObserver(() => recalc());
        ro.observe(el);
        roRef.current = ro;

        // Initial calc on next frame to ensure layout is ready
        requestAnimationFrame(recalc);
      }
    },
    [recalc],
  );

  // window resize fallback (helps when container resizes due to viewport changes)
  useEffect(() => {
    const onWin = () => recalc();
    window.addEventListener('resize', onWin);
    return () => window.removeEventListener('resize', onWin);
  }, [recalc]);

  return { rows, colWidth, colsNeeded, refCallback };
};
